import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "./../Layout";
import { useTheme } from "../../../Context/ThemeContext";
import { useNavigate } from "react-router-dom";

const Plane = () => {
  const navigate = useNavigate();

  const { darkMode } = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch plans from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(
          "https://bioopay-backendfull-code-production.up.railway.app/api/v1/plane/get-plane"
        );
        setPlans(response.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch plans");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);
  const handleBuyPlan = (planId) => {
    navigate("/dashboard/user/plan/purchase", { state: { planId } });
  };
  // Styles with dark mode support
  const styles = {
    container: {
      minHeight: "100vh",
      background: darkMode
        ? "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
        : "linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%)",
      padding: "48px 16px",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    header: {
      textAlign: "center",
      marginBottom: "48px",
      maxWidth: "1200px",
      marginLeft: "auto",
      marginRight: "auto",
    },
    title: {
      fontSize: "36px",
      fontWeight: "800",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      marginBottom: "16px",
    },
    subtitle: {
      fontSize: "20px",
      color: darkMode ? "#a0aec0" : "#4a5568",
      maxWidth: "600px",
      margin: "0 auto",
    },
    plansContainer: {
      display: "grid",
      gap: "32px",
      maxWidth: "1200px",
      margin: "0 auto",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    },
    planCard: {
      background: darkMode ? "#2d3748" : "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      position: "relative",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      border: darkMode ? "1px solid #4a5568" : "none",
    },
    popularBadge: {
      position: "absolute",
      top: "0",
      right: "0",
      background: "#4f46e5",
      color: "white",
      padding: "4px 12px",
      fontSize: "12px",
      fontWeight: "bold",
      borderBottomLeftRadius: "8px",
    },
    planContent: {
      padding: "32px",
    },
    planName: {
      fontSize: "24px",
      fontWeight: "700",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      marginBottom: "16px",
    },
    planPrice: {
      fontSize: "32px",
      fontWeight: "800",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      marginBottom: "8px",
    },
    pkrPrice: {
      fontSize: "16px",
      color: darkMode ? "#a0aec0" : "#4a5568",
      marginBottom: "16px",
    },
    commissionText: {
      color: darkMode ? "#a0aec0" : "#4a5568",
      marginBottom: "24px",
    },
    featureList: {
      marginBottom: "32px",
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      marginBottom: "12px",
      fontSize: "16px",
      color: darkMode ? "#e2e8f0" : "#4a5568",
    },
    checkIcon: {
      width: "20px",
      height: "20px",
      marginRight: "8px",
      color: "#10b981",
    },
    buttonGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    primaryButton: {
      padding: "12px 24px",
      borderRadius: "8px",
      fontWeight: "600",
      fontSize: "16px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      textAlign: "center",
      border: "none",
    },
    secondaryButton: {
      padding: "12px 24px",
      borderRadius: "8px",
      fontWeight: "600",
      fontSize: "16px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      textAlign: "center",
      border: "1px solid #4f46e5",
      color: "#4f46e5",
      background: "transparent",
    },
    helpSection: {
      background: darkMode ? "#2d3748" : "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      padding: "32px",
      maxWidth: "800px",
      margin: "48px auto 0",
      textAlign: "center",
      border: darkMode ? "1px solid #4a5568" : "none",
    },
    helpTitle: {
      fontSize: "24px",
      fontWeight: "700",
      color: darkMode ? "#ffffff" : "#1a1a1a",
      marginBottom: "16px",
    },
    helpText: {
      fontSize: "16px",
      color: darkMode ? "#a0aec0" : "#4a5568",
      marginBottom: "24px",
    },
    contactButton: {
      padding: "12px 24px",
      borderRadius: "8px",
      fontWeight: "600",
      fontSize: "16px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      background: darkMode ? "#4f46e5" : "#1a1a1a",
      color: "white",
      border: "none",
    },
    loadingText: {
      textAlign: "center",
      fontSize: "18px",
      color: darkMode ? "#a0aec0" : "#4a5568",
    },
    errorText: {
      textAlign: "center",
      fontSize: "18px",
      color: "#e53e3e",
    },
  };

  // Handle button clicks

  const handleActivatePlan = (planId) => {
    console.log(`Activating plan ${planId}`);
    // Add activation logic here
  };

  // Team earning focused features
  const getPlanFeatures = (planName) => {
    const commonFeatures = [
      "Team commission earnings",
      "Direct referral bonuses",
      "24/7 support",
      "Secure transactions",
    ];

    // if (planName === "Diamond Plan") {
    //   return [
    //     "60% commission rate",
    //     "Unlimited team levels",
    //     "Priority support",
    //     "Daily payout",
    //     ...commonFeatures,
    //   ];
    // } else if (planName === "Gold Plan") {
    //   return [
    //     "10% commission rate",
    //     "5 team levels",
    //     "Standard support",
    //     "Daily payout",
    //     ...commonFeatures,
    //   ];
    // }
    return commonFeatures;
  };

  // Convert USD to PKR (1 USD = 150 PKR)
  const convertToPKR = (usdAmount) => {
    return usdAmount * 150;
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading plans...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div style={styles.container}>
          <p style={styles.errorText}>{error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={"BIooPay- Plans"}>
      <div style={styles.container}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={styles.header}
        >
          <h1 style={styles.title}>Choose Your Plan</h1>
          <p style={styles.subtitle}>
            Select a plan to start earning with your team
          </p>
        </motion.div>

        <div style={styles.plansContainer}>
          {plans.map((plan, index) => (
            <motion.div
              key={plan._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{
                ...styles.planCard,
                transform: index === 0 ? "scale(1.03)" : "scale(1)",
                boxShadow:
                  index === 0
                    ? "0 20px 25px -5px rgba(0, 0, 0, 0.1)"
                    : styles.planCard.boxShadow,
              }}
              whileHover={{ scale: 1.02 }}
            >
              {index === 0 && (
                <div style={styles.popularBadge}>MOST POPULAR</div>
              )}

              <div style={styles.planContent}>
                <h3 style={styles.planName}>{plan.Planename}</h3>
                <p style={styles.planPrice}>${plan.price} /Lifetime</p>
                <p style={styles.pkrPrice}>{convertToPKR(plan.price)} PKR</p>
                <p style={styles.commissionText}>
                  Commission: {plan.commissionRate * 100}%
                </p>

                <ul style={styles.featureList}>
                  {getPlanFeatures(plan.Planename).map((feature, i) => (
                    <li key={i} style={styles.featureItem}>
                      <svg
                        style={styles.checkIcon}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <div style={styles.buttonGroup}>
                  <button
                    onClick={() => handleBuyPlan(plan._id)}
                    style={{
                      ...styles.primaryButton,
                      background:
                        index === 0
                          ? "#4f46e5"
                          : darkMode
                          ? "#4a5568"
                          : "#e0e7ff",
                      color:
                        index === 0
                          ? "white"
                          : darkMode
                          ? "#ffffff"
                          : "#4f46e5",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background =
                        index === 0
                          ? "#4338ca"
                          : darkMode
                          ? "#718096"
                          : "#d1d5ff";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background =
                        index === 0
                          ? "#4f46e5"
                          : darkMode
                          ? "#4a5568"
                          : "#e0e7ff";
                    }}
                  >
                    Activate Plan
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          style={styles.helpSection}
        >
          <h2 style={styles.helpTitle}>Need help choosing?</h2>
          <p style={styles.helpText}>
            Our team is here to help you select the perfect plan for maximum
            earnings.
          </p>
          <button
            style={styles.contactButton}
            onClick={() => {
              window.location.href = "kb9900350@gmail.com";
            }}
            onMouseOver={(e) => {
              e.target.style.background = darkMode ? "#4338ca" : "#000000";
            }}
            onMouseOut={(e) => {
              e.target.style.background = darkMode ? "#4f46e5" : "#1a1a1a";
            }}
          >
            Contact Support
          </button>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Plane;
